/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { addClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import {
  AsstRvwType,
  AsstTpCd,
  IdleAssetCondition,
  IdleAssetDetail,
  IdleAssetSummary,
  RvwStatCd,
} from 'models/asst/IdleAsset';
import { Code } from 'models/common/CommonCode';
import { findIdleAssetSummary } from 'apis/asst/IdleAsset';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { ComboBox } from '../../../components/selects/ComboBox';
import _, { toNumber } from 'lodash';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ManagementMode } from '../../../models/common/Common';

interface Prop {
  condition?: any;
}
/**
 * 불용자산현황(그래프) > 불용자산 요청 및 결과
 * @param props
 * @param ref
 * @constructor
 */
const IdleAssetRequestResultGrid = ({ condition }: Prop, ref) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<IdleAssetSummary[]>([]);
  const [code, setCode] = useState<any>();

  const defaultRowData = useMemo(
    () =>
      (code?.elmCopCd || []).reduce(
        (acc, cur) => [
          ...acc,
          {
            copCd: cur.cmnCd,
            eqpCount: 0,
            asstAcqrAmt: 0,
            deprAmt: 0,
            puseCount: 0,
            dpslCount: 0,
            dcdCount: 0,
          } as IdleAssetSummary,
        ],
        [
          {
            copCd: 'ALL',
            eqpCount: 0,
            asstAcqrAmt: 0,
            deprAmt: 0,
            puseCount: 0,
            dpslCount: 0,
            dcdCount: 0,
          },
        ]
      ),
    [code?.elmCopCd]
  );

  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    search: (params) => {
      findIdleAssetSummary('request', params).then((result) => {
        if (_.isNull(result) || result.length === 0) return;

        result.forEach((item) => {
          item.currCd = 'USD'; // 원래 재고 복사
        });
        //원 통화단위  저장
        setCurrCdPre('USD');

        const normalized = result.map((row) => ({
          ...row,
          eqpCount: row.eqpCount ?? 0,
          deprAmt: row.deprAmt ?? 0,
          puseCount: row.puseCount ?? 0,
        }));

        setRowData(normalized); //
      });
    },
  }));

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드
    const currCd: Code[] = await getCommonCodeNames('CURR_CD'); // 통화단위
    setCode({
      elmCopCd: elmCopCd,
      currCd: currCd,
    });
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.RowHeader === panel.cellType) {
      addClass(cell, 'transposed-header');
    }

    if (CellType.Cell === panel.cellType) {
      const item = panel.grid.itemsSource[col];

      if (row === 3) {
        if (item.puseCount > 0) {
          cell.style.textDecoration = 'underline';
          cell.style.color = '#2D9BB2';
          cell.style.cursor = 'pointer';
          cell.style.textUnderlinePosition = 'under';
        }
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e: MouseEvent) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);
      grid.startEditing(true);
      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      if (ht.panel === grid.cells) {
        const item = grid.itemsSource[ht.col];

        if (ht.row === 3) {
          const p = {
            ...condition,
            asstTpCd: AsstTpCd.EQUIPMENT,
            rvwRltDivsCd: 'PSBL',
          };

          if (!_.isEmpty(item.copCd) && 'ALL' != item.copCd) p['copCd'] = item.copCd;

          navigate('/asset/review-list', {
            state: {
              upprMnuUrl: location.pathname,
              mode: ManagementMode.READ,
              mnuId: `${location.pathname}`,
              mnuNm: t('asst.label.불용자산 검토목록', '불용자산 검토목록'),
              searchParam: p,
            },
          });
        }
      }
    });

    grid.loadedRows.addHandler((s, e) => {
      /*  setTimeout(() => {
        s.autoSizeColumn();
        s.invalidate();
      }, 50);*/
      try {
        const w: number = grid.hostElement.offsetWidth;
        const c: number = grid.itemsSource.length + 1;
        const calc = w / c;
        grid.columns.forEach((col, i) => {
          if (i === 0) {
            col.width = '*';
          } else {
            col.width = calc;
          }
        });
      } catch (e) {
        grid.columns.defaultSize = 100;
      }
      setTimeout(() => s.invalidate, 50);
    });
  };

  const layoutDefinition = [
    {
      binding: 'copCd',
      header: String(t('asst.label.구분', '구분')),
      align: 'center',
      width: 60,
      cellTemplate: (params) =>
        `${params.value === 'ALL' ? t('com.labe.전체', '전체') : params.value}`,
    },
    {
      binding: 'eqpCount',
      header: String(t('asst.label.자산 대수', '자산대수')),
      align: 'right',
      width: 60,
    },
    {
      binding: 'deprAmt',
      header: String(t('asst.label.잔존가액', '잔존가액')),
      align: 'right',
      width: 60,
    },
    {
      binding: 'puseCount',
      header: String(t('asst.label.활용', '활  용')),
      align: 'right',
      width: 60,
    },
    // {
    //   binding: 'dpslCount',
    //   header: String(t('asst.label.매각', '매각')),
    //   align: 'center',
    // },
    // {
    //   binding: 'dcdCount',
    //   header: String(t('asst.label.폐기', '폐기')),
    //   align: 'center',
    // },
  ];

  const [getCurrCdPre, setCurrCdPre] = useState<string>('');

  const setCurrChangeView = async (value) => {
    const tgtCurr = value == '' ? getCurrCdPre : value;
    //환율찾기
    const currCdTgt = code.currCd.filter((o) => o.cmnCd === tgtCurr);
    const currCdSrc = code.currCd.filter((o) => o.cmnCd === rowData[0].currCd);
    //값변경..
    setRowData((prev) =>
      prev.map((row) => {
        const deprAmt = parseFloat(String(row.deprAmt ?? 0));
        const numFrom = parseFloat(currCdSrc[0].optValCtn2.replace(/,/g, ''));
        const numTo = parseFloat(currCdTgt[0].optValCtn2.replace(/,/g, ''));
        const deprAmtCal = (deprAmt * numFrom) / numTo;
        return {
          ...row,
          currCd: tgtCurr,
          deprAmt: deprAmtCal,
        };
      })
    );
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('asst.label.불용자산 요청 및 결과', '불용자산 요청 및 결과')}</h3>
        </SubTitleGroup>
        <ControlBtnGroup>
          <ComboBox
            defaultValue={getCurrCdPre}
            options={code?.currCd}
            placeholder={String(t('com.label.통화단위', '통화단위'))}
            onChange={(value) => {
              setCurrChangeView(value);
            }}
            style={{ width: '120px' }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={(rowData || []).length > 0 ? rowData : defaultRowData}
        height={146}
        isTransposed={true}
        isReadOnly={true}
        isSelector={false}
        isFilter={false}
        allowPinning={false}
        initialized={onInitialized}
        /*loadedRows={(grid, e) => {
          grid.columns.defaultSize = 60; //;124.46;
          // 그리드를 새로 고칩니다.

          console.log('grid', grid);
          grid.collectionView.refresh();
        }}*/
      />
    </>
  );
};

export default React.forwardRef(IdleAssetRequestResultGrid);
